import React from 'react';

import StandardProductFilterSortModal from './StandardProductFilterSortModal';
import { ToolbarControl, ToolbarButton } from '../../../controls/ToolbarControl';
import { ToggleSwitchControl } from '../../../controls/ToggleSwitchControl';
import { ApiDataAccess } from '../../../infrastructure/ApiDataAccess';
import { StandardProduct } from './StandardProduct';

import './StandardProductListComponent.css';


const SORTABLE_CONFIG_FIELDS = [
    { id: 'name', label: 'Name' },
    { id: 'packagingType', label: 'Packaging Type' },
    { id: 'dimensions', label: 'Dimensions' },
    { id: 'material', label: 'Material' },
    { id: 'zipper', label: 'Zipper' },
    { id: 'roundedCorners', label: 'Rounded Corners' },
    { id: 'hangHole', label: 'Hang Hole' },
    { id: 'doubleCut', label: 'Double Cut' },
    { id: 'tearNotch', label: 'Tear Notch' },
    { id: 'sealWidth', label: 'Seal Width' },
    { id: 'valve', label: 'Valve' },
    { id: 'gussetType', label: 'Gusset Type' },
];

const FILTER_OPTIONS = {
    packagingType: [],
    material: [],
    zipper: [],
    roundedCorners: [],
    hangHole: [],
    doubleCut: [],
    tearNotch: [],
    sealWidth: [],
    valve: [],
    gussetType: []
};

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE_INDEX = 0;
export class StandardProductListComponent extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isFilterModalOpen: false,
            appliedFilters: null,
            appliedSort: [],
            items: [],
            isLoadingList: true,
            currentPage: DEFAULT_PAGE_INDEX,
            pageSize: DEFAULT_PAGE_SIZE,
            totalPageCount: 0,
            listError: null,
            isFirst: true,
            isLast: true,
            exportRequested: false
        };
        this.activeApiFilterString = null;
        this.filterOptionsConfig = FILTER_OPTIONS;
        this.sortableFieldsConfig = SORTABLE_CONFIG_FIELDS;

        this.handleOpenFilterModal = this.handleOpenFilterModal.bind(this);
        this.handleCloseFilterModal = this.handleCloseFilterModal.bind(this);
        this.handleSaveFilters = this.handleSaveFilters.bind(this);
        this.handleResetFilters = this.handleResetFilters.bind(this);
        this.buildApiFilterString = this.buildApiFilterString.bind(this);
        this.fetchListData = this.fetchListData.bind(this);
        this.handlePageChange = this.handlePageChange.bind(this);
        this.handlePageSizeChange = this.handlePageSizeChange.bind(this);
        this.onPublishToggle = this.onPublishToggle.bind(this);
        this.formatDimensions = this.formatDimensions.bind(this);
        this.getOffset = this.getOffset.bind(this);
        this.getUom = this.getUom.bind(this);
        this.sortItems = this.sortItems.bind(this); // Add binding for new sort function
    }

    componentDidMount() {
        this.fetchListData(DEFAULT_PAGE_INDEX, this.state.pageSize);
    }

    sortItems(itemsToSort, sortCriteria) {
        if (!sortCriteria || sortCriteria.length === 0 || !itemsToSort || itemsToSort.length === 0) {
            return itemsToSort;
        }

        const getSortValue = (item, fieldId) => {
            switch (fieldId) {
                case 'name':
                    return item.name || '';
                case 'packagingType':
                    return item.convertingMethod?.name || '';
                case 'dimensions':
                    // Return an object for multi-level sort within the comparator
                    return {
                        width: parseFloat(item.width) || 0,
                        length: parseFloat(item.length) || 0
                    };
                case 'material':
                    // Sort by ID for consistency (can change if name needed)
                    return item.structureSpecId || null;
                case 'zipper':
                    return item.zipper?.name || '';
                case 'roundedCorners':
                    return item.roundedCorners?.name || '';
                case 'hangHole':
                    return item.hangHole?.name || '';
                case 'doubleCut':
                    return item.doubleCut?.name || '';
                case 'tearNotch':
                    return item.tearNotch?.name || '';
                case 'sealWidth':
                    return item.sealWidth?.name || '';
                case 'valve':
                    return item.valve?.name || '';
                case 'gussetType':
                    return item.gussetType?.name || '';
                default:
                    return item[fieldId] || null;
            }
        };

        const sortedItems = [...itemsToSort];

        sortedItems.sort((a, b) => {
            for (const sortField of sortCriteria) {
                const fieldId = sortField.id;

                if (fieldId === 'dimensions') {
                    const valA = getSortValue(a, fieldId);
                    const valB = getSortValue(b, fieldId);
                    // Compare width first
                    if (valA.width < valB.width) return -1;
                    if (valA.width > valB.width) return 1;
                    // If widths are equal, compare length
                    if (valA.length < valB.length) return -1;
                    if (valA.length > valB.length) return 1;
                } else {
                    const valA = getSortValue(a, fieldId);
                    const valB = getSortValue(b, fieldId);

                    // Basic comparison, assumes ascending order ('asc')
                    const compA = valA ?? ''; // Treat null/undefined as empty string for comparison safety
                    const compB = valB ?? '';

                    if (typeof compA === 'string' && typeof compB === 'string') {
                        const comparison = compA.localeCompare(compB);
                        if (comparison !== 0) return comparison;
                    } else { // Handle numbers or other types
                        if (compA < compB) return -1;
                        if (compA > compB) return 1;
                    }
                }
            }
            return 0;
        });

        return sortedItems;
    }


    fetchListData(pageIndex, pageSize) {
        this.setState({ isLoadingList: true, listError: null });

        const filterString = this.buildApiFilterString(this.state.appliedFilters);

        const apiBasePath = '/data';
        let url = `${apiBasePath}/standard-product/pagable/pagable?pageIndex=${pageIndex}&pageSize=${pageSize}`;
        if (filterString) {
            url += `&${filterString}`;
        }

        fetch(url)
            .then(response => {
                if (!response.ok) {
                    return response.text().then(text => {
                        throw new Error(`HTTP error ${response.status}: ${text || 'Failed to fetch list data'}`);
                    });
                }
                const contentType = response.headers.get("content-type");
                if (contentType && contentType.indexOf("application/json") !== -1) {
                    return response.json();
                } else {
                    return response.text().then(text => { throw new Error(`Expected JSON list data, received ${contentType}`) });
                }
            })
            .then(data => {
                this.filterOptionsConfig = this.processDataForFilters(data.items);

                // Apply client-side sorting AFTER data is fetched
                const fetchedItems = data.items || [];
                const sortedItems = this.sortItems(fetchedItems, this.state.appliedSort);

                this.setState({
                    items: sortedItems,
                    totalPageCount: data.totalPageCount || 0,
                    currentPage: data.pageIndex !== undefined ? data.pageIndex : pageIndex,
                    pageSize: pageSize,
                    isLoadingList: false,
                    listError: null,
                    isFirst: data.isFirst,
                    isLast: data.isLast,
                });
            })
            .catch(error => {
                console.error("Error fetching list data:", error);
                this.setState({
                    isLoadingList: false,
                    listError: error.message || "Failed to load products.",
                    items: [],
                    totalPageCount: 0,
                    isFirst: true,
                    isLast: true,
                });
            });
    }


    processDataForFilters(items) {
        const mappings = {
            packagingType: { id: 'convertingMethodId', name: 'convertingMethod.name' },
            material: { id: 'structureSpecId', name: 'structureSpec.name', formatName: (id) => `SPEC-${id}` },
            zipper: { id: 'zipperId', name: 'zipper.name' },
            roundedCorners: { id: 'roundedCornersId', name: 'roundedCorners.name' },
            hangHole: { id: 'hangHoleId', name: 'hangHole.name' },
            doubleCut: { id: 'doubleCutId', name: 'doubleCut.name' },
            tearNotch: { id: 'tearNotchId', name: 'tearNotch.name' },
            sealWidth: { id: 'sealWidthId', name: 'sealWidth.name' },
            valve: { id: 'valveId', name: 'valve.name' },
            gussetType: { id: 'gussetTypeId', name: 'gussetType.name' }
        };

        // Helper function to get nested property value safely
        const getNestedValue = (obj, path) => {
            if (!path) return undefined;
            return path.split('.').reduce((acc, part) => acc && acc[part], obj);
        };

        // Clear existing options before reprocessing
        for (const key in FILTER_OPTIONS) {
            FILTER_OPTIONS[key] = [];
        }

        if (items && items.length > 0) {
            items.forEach(item => {
                for (const key in mappings) {
                    const mapping = mappings[key];
                    const id = item[mapping.id];

                    let name = getNestedValue(item, mapping.name);

                    if (mapping.formatName && id != null) {
                        name = mapping.formatName(id);
                    }

                    if (id != null && name != null && name.toString().trim() !== '') {
                        // Check if an object with this ID already exists in the target array
                        const exists = FILTER_OPTIONS[key].some(option => option.id === id);
                        if (!exists) {
                            FILTER_OPTIONS[key].push({ id: id, name: name });
                        }
                    }
                }
            });
        } else {
            console.warn("No items received to process for filters.");
        }

        // --- Sort the filter option arrays AFTER processing all items ---
        for (const key in FILTER_OPTIONS) {
            if (Array.isArray(FILTER_OPTIONS[key])) {
                FILTER_OPTIONS[key].sort((a, b) => {
                    // Handle potential null/undefined names during sort
                    const nameA = a.name || '';
                    const nameB = b.name || '';
                    // Ensure localeCompare is called on strings
                    return String(nameA).localeCompare(String(nameB));
                });
            }
        }
        return FILTER_OPTIONS;
    }

    handleOpenFilterModal() { this.setState({ isFilterModalOpen: true }); }
    handleCloseFilterModal() { this.setState({ isFilterModalOpen: false }); }

    handleResetFilters() {
        this.activeApiFilterString = null;
        this.setState({
            appliedFilters: null,
            appliedSort: [],
            isFilterModalOpen: false,
            currentPage: DEFAULT_PAGE_INDEX
        }, () => {
            this.fetchListData(DEFAULT_PAGE_INDEX, this.state.pageSize);
        });
    }

    handleSaveFilters(filtersFromModal, sortOrderFromModal) {
        const apiFilterString = this.buildApiFilterString(filtersFromModal);
        this.activeApiFilterString = apiFilterString;

        this.setState({
            appliedFilters: filtersFromModal,
            appliedSort: sortOrderFromModal,
            isFilterModalOpen: false,
            currentPage: DEFAULT_PAGE_INDEX
        }, () => {
            this.fetchListData(DEFAULT_PAGE_INDEX, this.state.pageSize);
        });
    }

    handlePageChange(newPage) {
        if (newPage !== this.state.currentPage) {
            this.setState({ currentPage: newPage });
            this.fetchListData(newPage, this.state.pageSize);
        }
    }

    handlePageSizeChange(newPageSize) {
        if (newPageSize !== this.state.pageSize) {
            this.setState({ currentPage: DEFAULT_PAGE_INDEX, pageSize: newPageSize });
            this.fetchListData(DEFAULT_PAGE_INDEX, newPageSize);
        }
    }


    buildApiFilterString(filters) {
        const filterParts = [];
        if (filters) {
            const dMap = {
                width: 'width',
                length: 'length',
                gusset: 'gusset'
            };
            Object.keys(dMap).forEach(k => {
                const apiF = dMap[k];
                if (filters[k]?.from) {
                    const v = parseFloat(filters[k].from);
                    if (!isNaN(v))
                        filterParts.push(`${apiF}>=${v}`);
                }
                if (filters[k]?.to) {
                    const v = parseFloat(filters[k].to);
                    if (!isNaN(v))
                        filterParts.push(`${apiF}<=${v}`);
                }
            });
            const cMap = {
                packagingType: 'converting_method_id',
                material: 'structure_spec_id',
                zipper: 'zipper_id',
                roundedCorners: 'rounded_corners_id',
                hangHole: 'hang_hole_id',
                doubleCut: 'double_cut_id',
                tearNotch: 'tear_notch_id',
                sealWidth: 'seal_width_id',
                valve: 'valve_id',
                gussetType: 'gusset_type_id'
            };
            Object.keys(cMap).forEach(k => {
                const selVals = filters[k];
                if (Array.isArray(selVals) && selVals.length > 0) {
                    const apiF = cMap[k];
                    filterParts.push(`${apiF} IN (${selVals.join(',')})`); // Ensure values are comma-separated if needed by API
                }
            });
        }

        let filterQuery = filterParts.length > 0 ? `filter=${filterParts.join(' AND ')}` : '';

        return filterQuery;
    }


    getFormattedDate(dateStr) { if (dateStr) { const date = new Date(dateStr); return date.toLocaleDateString(); } return null; }
    isPublished(item) {
        return item.isPublished === true ? true : false;
    }
    formatDimensions(length, width, gusset) { const l = parseFloat(length); const w = parseFloat(width); const g = parseFloat(gusset); if (isNaN(l) || isNaN(w)) return "Invalid Dimensions"; const formatNum = (n) => n.toFixed(3); if (!isNaN(g) && g > 0) return `${formatNum(w)}(W) x ${formatNum(l)}(L) x ${formatNum(g)}(G)`; return `${formatNum(w)}(W) x ${formatNum(l)}(L)`; }
    getUom(uomId) { switch (uomId) { case 13: return "in"; case 15: return "mm"; default: return "in"; } }
    getOffset(value, uomId) { if (!value) return ""; const uom = this.getUom(uomId); const val = parseFloat(value); return `${!isNaN(val) ? val.toFixed(2) : value}${uom ? ` ${uom}` : ''}`; }


    onPublishToggle(item, value, toggle) {

        const currentPublishedState = this.isPublished(item); // Use helper
        const standardProduct = new StandardProduct(item.id);

        const action = currentPublishedState
            ? standardProduct.unpublish.bind(standardProduct)
            : standardProduct.publish.bind(standardProduct);

        action()
            .then(() => {
                window.Alerts.showSuccess(`Standard Product ${item.name} updated.`);
                this.fetchListData(this.state.currentPage, this.state.pageSize); // Refetch current page
            })
            .catch(ex => {
                console.error("Publish error:", ex);
                let msg = `Failed update.`;
                if (ex?.exceptions?.[0]?.message) msg += ` ${ex.exceptions[0].message}`;
                else if (ex?.messages?.[0]) msg += `$ {ex.messages[0]}`;
                window.Alerts.showError(msg);

                // Revert UI toggle on failure
                if (toggle && typeof toggle.setValue === 'function') {
                    toggle.setValue(currentPublishedState);
                }
            });
    }

    async onExportClick() {
        var dataAccess = new ApiDataAccess('/data/standard-product');
        var data = await dataAccess.post('/prices/export', {});

        this.setState({ exportRequested: true })

        window.Modal.show(
            modal => {
                return (
                    <div>
                        <div>
                            <p>Your requested Data Export has been scheduled. You will be notified by email once it becomes available.</p>
                        </div>
                    </div>
                )
            },
            'Data Export',
            modal => {
                return (
                    <div>
                        <button className='btn btn-secondary pull-right' onClick={() => { modal.close() }}>Close</button>
                    </div>
                )
            }
        )


    }


    render() {
        const {
            isFilterModalOpen, appliedFilters, appliedSort,
            items, isLoadingList, listError, currentPage, pageSize, totalPageCount, isFirst, isLast
        } = this.state;
        // Determine if active filters OR sort criteria exist
        const hasActiveFiltersOrSort = !!appliedFilters || (appliedSort && appliedSort.length > 0);
        var self = this;
        //        console.log("## items: ", items)
        return (
            <div className='standard-product-details'>
                <ToolbarControl renderButtons={() => (
                    <div>
                        <ToolbarButton
                            icon='glyphicon-plus'
                            text='New'
                            onClick={() => window.Router.setRoute('/standard-products/new')}
                        />
                        <ToolbarButton
                            icon={hasActiveFiltersOrSort ? 'glyphicon glyphicon-remove' : 'glyphicon glyphicon-filter'}
                            text={hasActiveFiltersOrSort ? 'Reset' : 'Filter & Sort'}
                            onClick={hasActiveFiltersOrSort ? this.handleResetFilters : this.handleOpenFilterModal}
                            disabled={isLoadingList}
                        />
                        <ToolbarButton
                            icon='glyphicon-export'
                            text='Export'
                            onClick={() => { this.onExportClick() }}
                            disabled={this.state.exportRequested}
                        />
                    </div>
                )} />

                {listError && <div style={{ color: 'red', padding: '10px' }}>Error loading products: {listError}</div>}


                <PageableStandardProductDataObjectList
                    items={items}
                    isLoading={isLoadingList}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    totalPageCount={totalPageCount}
                    isFirst={isFirst}
                    isLast={isLast}
                    onPageChange={this.handlePageChange}
                    onPageSizeChange={this.handlePageSizeChange}
                    onInit={listInstance => { /* Store instance if needed */ }}
                    onRenderTitleRow={() => (
                        <div className='standard-product-header-row'>
                            <div className='col pull-left col-lock-icon' />
                            <div className='col col-1 pull-left'>Name</div>
                            <div className='col col-2 pull-left'>Pouch Type</div>
                            <div className='col col-2 pull-left'>Dimensions</div>
                            <div className='col col-1 pull-left'>Material</div>
                            <div className='col col-4 pull-left'>Features</div>
                            <div className='col col-1 pull-right'>Published</div>
                        </div>
                    )}
                    onRenderRow={(item) => (
                        <div key={`row_${item.id}`} className='standard-product-row' onClick={() => { window.Router.setRoute(`/standard-products/${encodeURIComponent(item.id)}`) }}>
                            <div>
                                <div className='col pull-left col-lock-icon'> {item.isImmutable ? <span className='glyphicon glyphicon-lock lock' /> : <span />} </div>
                                <div className='col col-1 pull-left '>{item.name}</div>
                                <div className='col col-2 pull-left'>{item.convertingMethod?.name} </div>
                                <div className='col col-2 pull-left'>{this.formatDimensions(item.length, item.width, item.gusset)} </div>
                                <div className='col col-1 pull-left'>{item.structureSpecId ? `SPEC-${item.structureSpecId}` : ''} </div>
                                <div className='col col-4 feature-column'>
                                    <div className="col col-8 feature-column-left">
                                        <div className="col col-12 feature-label"><label>Zipper:</label></div><div className="col col-8 feature-value">{item.zipper?.name}</div><div className="col col-4 ">{this.getOffset(item.zipperOffset, item.zipper?.defaultLocationUomId)}</div>
                                        <div className="col col-12 feature-label"><label>Hang Hole:</label></div><div className="col col-8 feature-value">{item.hangHole?.name}</div><div className="col col-4 ">{this.getOffset(item.hangHoleOffset, item.hangHole?.defaultLocationUomId)}</div>
                                        <div className="col col-12 feature-label"><label>Tear Notch:</label></div><div className="col col-8 feature-value">{item.tearNotch?.name}</div><div className="col col-4 ">{this.getOffset(item.tearNotchOffset, item.tearNotch?.defaultLocationUomId)}</div>
                                        <div className="col col-12 feature-label"><label>Valve:</label></div><div className="col col-8 feature-value">{item.valve?.name}</div><div className="col col-4 ">{this.getOffset(item.valveOffset, item.valve?.defaultLocationUomId)}</div>
                                    </div>
                                    <div className="col col-4 feature-column-right">
                                        <div className="feature-label"><label>Rounded Corners:</label></div><div className="feature-value">{item.roundedCorners?.name}</div>
                                        <div className="feature-label"><label>Double Cut:</label></div><div className="feature-value">{item.doubleCut?.name}</div>
                                        <div className="feature-label"><label>Gusset Type:</label></div><div className="feature-value">{item.gussetType?.name}</div>
                                        <div className="feature-label"><label>Seal Width:</label></div><div className="feature-value">{item.sealWidth?.name}</div>
                                    </div>
                                </div>
                            </div>
                            <div className='col col-1 pull-right toggle-column-container' onClick={(e) => e.stopPropagation()} >
                                <ToggleSwitchControl id={`toggle_spec_${item.id}`} on={this.isPublished(item)} onChange={(value, cntrl) => self.onPublishToggle(item, value, cntrl)} aria-label={`Toggle publish status for ${item.name}`} />
                            </div>
                        </div>
                    )}
                />

                {isFilterModalOpen && (
                    <StandardProductFilterSortModal
                        isOpen={isFilterModalOpen}
                        onClose={this.handleCloseFilterModal}
                        onSave={this.handleSaveFilters}
                        initialFilters={this.state.appliedFilters}
                        initialSort={this.state.appliedSort} // Pass current sort state
                        filterOptions={this.filterOptionsConfig}
                        sortableFields={this.sortableFieldsConfig}
                    />
                )}
            </div>
        );
    }
}




export class PageableStandardProductDataObjectList extends React.Component {
    constructor(props) {
        super(props);

        if (!this.props.onRenderRow) {
            throw new Error("onRenderRow Property is required for PageableDataObjectListTest");
        }

        this.state = {};

        this.onPageSizeChanged = this.onPageSizeChanged.bind(this);
        this.onFirstClick = this.onFirstClick.bind(this);
        this.onPreviousClick = this.onPreviousClick.bind(this);
        this.onNextClick = this.onNextClick.bind(this);
        this.onLastClick = this.onLastClick.bind(this);
    }

    componentDidMount() {
        if (this.props.onInit) {
            this.props.onInit(this);
        }
    }


    onPageSizeChanged(event) {
        const newPageSize = parseInt(event.target.value, 10);
        if (this.props.onPageSizeChange) {
            this.props.onPageSizeChange(newPageSize);
        }
    }

    onFirstClick() {
        if (this.props.onPageChange) {
            this.props.onPageChange(0);
        }
    }

    onPreviousClick() {
        const currentPage = this.props.currentPage || 0;
        if (currentPage > 0 && this.props.onPageChange) {
            this.props.onPageChange(currentPage - 1);
        }
    }

    onNextClick() {
        const currentPage = this.props.currentPage || 0;
        const totalPageCount = this.props.totalPageCount || 0;

        if (currentPage < totalPageCount - 1 && this.props.onPageChange) {
            this.props.onPageChange(currentPage + 1);
        }
    }

    onLastClick() {
        const totalPageCount = this.props.totalPageCount || 0;

        if (totalPageCount > 0 && this.props.onPageChange) {
            this.props.onPageChange(totalPageCount - 1);
        }
    }

    render() {
        const {
            items = [],
            isLoading = false,
            currentPage = 0,
            pageSize = 20,
            totalPageCount = 0,
            isFirst = true,
            isLast = true,
            onRenderTitleRow,
            onRenderRow,
            onPageChange,
            onPageSizeChange
        } = this.props;


        const pageIndexDisplay = currentPage + 1; // Convert 0-based to 1-based for display
        const currentPageSize = pageSize;

        const paginationDisabled = !onPageChange || !onPageSizeChange || isLoading;

        return (
            <div className="generic-component">
                <div className="generic-list-component">
                    <div className="paging">
                        <div className="button-section pull-left">
                            <button className="btn btn-primary btn-sm no-marging-left" disabled={isFirst || paginationDisabled} onClick={this.onFirstClick}><span className="glyphicon glyphicon-fast-backward"></span></button>
                            <button className="btn btn-primary btn-sm no-marging-left" disabled={isFirst || paginationDisabled} onClick={this.onPreviousClick}><span className="glyphicon glyphicon-step-backward"></span></button>
                        </div>

                        <div className='filter mobile-hidden'>
                            <select id="page_size_selector" className="control dropdown valid" onChange={this.onPageSizeChanged} value={currentPageSize} disabled={paginationDisabled}>
                                <option value='20'>20 Items</option>
                                <option value='50'>50 Items</option>
                                <option value='100'>100 Items</option>
                                <option value='200'>200 Items</option>
                            </select>
                        </div>

                        <div className="col">
                        </div>

                        <div className="filter mobile-hidden">
                            <div className="center">
                                {totalPageCount > 0 ? `${pageIndexDisplay} of ${totalPageCount}` : '0 of 0'}
                            </div>
                        </div>

                        <div className="button-section">
                            <button className="btn btn-primary btn-sm no-marging-right pull-right" disabled={isLast || paginationDisabled} onClick={this.onNextClick}><span className="glyphicon glyphicon-step-forward"></span></button>
                            <button className="btn btn-primary btn-sm no-marging-right pull-right" disabled={isLast || paginationDisabled} onClick={this.onLastClick}><span className="glyphicon glyphicon-fast-forward"></span></button>
                        </div>
                    </div>

                    <div className='table'>
                        {onRenderTitleRow &&
                            <div className='table-header'>
                                {onRenderTitleRow()}
                            </div>
                        }

                        {isLoading ? (
                            <div className='body' style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>
                        ) : items && items.length > 0 ? (
                            <div className='body'>
                                {items.map((item, idx) => {
                                    const key = item && item.id !== undefined ? item.id : idx;
                                    return (
                                        <React.Fragment key={`row-${key}`}>
                                            {onRenderRow(item, idx)}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className='body' style={{ textAlign: 'center', padding: '20px' }}>No items found.</div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}